import { WToast } from 'react-native-smart-tip';
import Storage from '@aws-amplify/storage';
import { Auth, DataStore } from 'aws-amplify';
import { AppUser, Varieties, DiagnoseType } from '../../models';
import React, { useEffect, useState } from 'react';
import { View, Text, Image, ImageStyle, ImageURISource, ImageResizeMode } from 'react-native';
import * as AppConst from './AppConst';

/** ================================
*  共通処理
*  ================================*/

/**
 * 日時フォーマット
 * Date型をyyyy-MM-ddに変換する
 */
export const formatDate = (date?: Date) => {
  if (date == undefined) {
    return '';
  } else {
    const yyyy = date.getFullYear();
    const MM = ('0' + (date.getMonth() + 1)).slice(-2);
    const dd = ('0' + date.getDate()).slice(-2);
    return yyyy + '-' + MM + '-' + dd;
  }
};

/**
 * 日時フォーマット
 * yyyy-MM-ddをDate型に変換する
 */
export const formatToDate = (date: string) => {
  if (date == undefined || date.length < 10) {
    return new Date();
  }
  const tmp = date.split('-');
  if (tmp.length != 3) {
    return new Date();
  }
  const yyyy = Number(tmp[0]);
  const MM = Number(tmp[1]) - 1;
  const dd = Number(tmp[2]);
  return new Date(yyyy, MM, dd);
};

/**
 * 日時フォーマット
 * Date型をyyyy/MM/ddに変換する
 */
export const formatDateAndWeek = (date: Date) => {
  if (date == undefined) {
    return '';
  } else {
    const yyyy = date.getFullYear();
    const MM = (date.getMonth() + 1).toString().slice(-2);
    const dd = date.getDate().toString().slice(-2);
    const dayOfWeek = date.getDay();
    const dayOfWeekStr = ["日", "月", "火", "水", "木", "金", "土"][dayOfWeek];
    return yyyy + '年' + MM + '月' + dd + '日' + '(' + dayOfWeekStr + ')';
  }
};

/**
 * 日付フォーマット
 * Date型をyyyy年MM月dd日(date)に変換する
 */
export const formatToJapanDate = (item: Date): string => {
  const days = ["日", "月", "火", "水", "木", "金", "土"];
  const year = item.getFullYear();
  const month = item.getMonth() + 1;
  const date = item.getDate();
  const day = days[item.getDay()];
  return year + "年" + month + "月" + date + "日(" + day + ")";
};

/**
 * 現在時刻取得
 */
export const getCurrentDateTime = () => {
  const current = new Date();
  const yyyy = current.getFullYear();
  const MM = ('0' + (current.getMonth() + 1)).slice(-2);
  const dd = ('0' + current.getDate()).slice(-2);
  const HH = ('0' + current.getHours()).slice(-2);
  const mm = ('0' + current.getMinutes()).slice(-2);
  const ss = ('0' + current.getSeconds()).slice(-2);

  return yyyy + '/' + MM + '/' + dd + ' ' + HH + ':' + mm + ':' + ss;
};

/**
 * 配列要素マージ
 */
export const mergeList = (base: string[], add: string[]) => {
  for (let i = 0; i < add.length; i++) {
    const index = base.indexOf(add[i]);
    if (index < 0) {
      base.push(add[i]);
    }
  }
};

/**
 * メッセージ表示
 */
export const appMsg = async (msg: string) => {
  WToast.show({
    data: msg,
    textColor: '#ffffff',
    backgroundColor: '#7AAD47',
    duration: WToast.duration.SHORT,
    position: WToast.position.CENTER,
  });
};

/**
 * エラーメッセージ表示
 */
export const appAlert = async (msg: string) => {
  WToast.show({
    data: msg,
    textColor: '#ffffff',
    backgroundColor: '#cc2f18',
    duration: WToast.duration.LONG,
    position: WToast.position.CENTER,
  });
};

/**
 * ローディング表示
 */
let loadingFunc: any = null;
export const setLoadingFunc = (func: Function) => {
  loadingFunc = func;
};
export const startLoading = () => {
  loadingFunc(true);
};
export const endLoading = () => {
  loadingFunc(false);
};

/**
 * ファイル名から拡張子を取得する関数
 */
export function getExt(filename: string) {
  return filename.toString().slice(filename.indexOf('/') + 1, filename.indexOf(';'));
}

/**
 * アップロード予定のファイル名の拡張子が許可されているか確認する関数
 */
export function checkExt(filename: string) {
  //アップロードを許可する拡張子
  var allow_exts = new Array('jpg', 'jpeg', 'png');

  //許可する拡張子の一覧(allow_exts)から対象の拡張子があるか確認する
  if (allow_exts.indexOf(filename) === -1) {
    return false;
  }
  return true;
}

/**
 * Cognitoエラーハンドリング
 */
export function handleCognitoErrorMessage(error: any) {
  let errorObject = {
    code: error.code,
    message: "",
  };

  switch (error.code) {
    case "UserNotConfirmedException":
      errorObject.message = "ユーザが認証されていません。";
      break;
    case "PasswordResetRequiredException":
      errorObject.message = "パスワードを変更してください";
      break;
    case "NotAuthorizedException":
      errorObject.message = "ユーザー名またはパスワードが違います。";
      break;
    case "UserNotFoundException":
      errorObject.message = "ユーザが存在しません。";
      break;
    case "InvalidParameterException":
      errorObject.message = "必要な情報が入力されていません。";
      break;
    case "CodeMismatchException":
      errorObject.message = "入力されたコードが無効です。";
      break;
    case "LimitExceededException":
      errorObject.message = "試行制限を超えました。しばらくしてから試してください。";
      break;
    case "ExpiredCodeException":
      errorObject.message = "入力されたコードが有効期限切れです。";
      break;
    default:
      errorObject.message = "例外が発生しました。";
  }
  return errorObject;
}

/**
 * ログインユーザーのユーザー情報取得
 */
export const getCurrentUser = async () => {
  const currentSession = await Auth.currentSession();
  const { payload } = currentSession.getIdToken();
  const email = payload['email'];

  const appUser = (await DataStore.query(AppUser, c => c.authUserID('eq', email)));
  return appUser.length > 0 ? appUser[0] : undefined;
};

/**
 * blob化(S3に画像をアップロードする際に使用する)
 */
export const blobConvert = async (uri: string) => {
  const response = await fetch(uri);
  const blob = await response.blob();
  return blob;
};

/**
 * S3Image表示
 */
export const S3Image = ({ imageKey, style, defaultSource, resizeMode = 'contain' }: { imageKey?: string | undefined | null, style?: ImageStyle | undefined, defaultSource?: ImageURISource, resizeMode?: ImageResizeMode; }) => {
  const [uri, setUri] = useState<string>('');

  useEffect(() => {
    (async () => {
      if (imageKey != undefined && imageKey != null) {
        const tmpUri = await Storage.get(imageKey);
        setUri(tmpUri);
      }
    })();
  }, [imageKey]);

  return (
    <Image resizeMode={resizeMode} source={{ uri: uri }} style={style} defaultSource={defaultSource} />
  );
};

/**
 * バッジ表示
 */
export const Badge = ({ count }: { count: number; }) => {
  return (
    count > 0 ?
      <View style={AppConst.styles.badgeContainer}>
        <Text style={AppConst.styles.badge}>{count}</Text>
      </View>
      :
      <></>
  );
};

/** 
 * カテゴリー表示名取得
 */
export function getCategoryName(category: string, categoryOptional?: string | null, shorten?: boolean) {
  if (categoryOptional) {
    if (shorten && categoryOptional.length > 7) {
      return categoryOptional.slice(0, 7) + '…'
    }
    return categoryOptional
  }
  switch (category) {
    case "flowering":
      return "開花";
    case "pesticideSprayning":
      return "農薬散布";
    case "fruitsThinning":
      return "摘果作業";
    case "mating":
      return "交配";
    case "harvest":
      return "収穫";
    case "others":
      return "その他";
    default:
      return "その他";
  }
};

/**
 * 0を左詰め
 */
export function fillZero(num: number, zeroCount: number): string {
  let zeros: string = "";
  for (let i = 0; i < zeroCount; i++) {
    zeros += "0";
  };
  return ((zeros + num.toString()).slice(-zeroCount));
};

/**
 * await sleep(3000)で３秒間スリープする
 * @param msec {number} スリープしたい時間をミリ秒で入力
 * @returns 
 */
export const sleep = (msec: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(() => resolve(), msec);
  });
};

/**
 * 栽培品種を日本語に変換
 */
export function varietiesCnv(key: Varieties | string) {
  switch (key) {
    case Varieties.KOUSUI:
      return "幸水";
    case Varieties.HOUSUI:
      return "豊水";
    case Varieties.AKIZUKI:
      return "あきづき";
    case Varieties.NIITAKA:
      return "新高";
    default:
      return "その他";
  }
};

/**
 * 診断タイプを単位に変換
 */
export function getDiagnoseTypeUnit(key: DiagnoseType | string) {
  switch (key) {
    case DiagnoseType.VEGETATION_COVERAGE:
    case DiagnoseType.FLOWERING_RATE:
      return "%";
    case DiagnoseType.BEARING_NUMBER:
      return "個";
    case DiagnoseType.FRUIT_SIZE:
    case DiagnoseType.FRUIT_MAX_SIZE:
    case DiagnoseType.FRUIT_MIN_SIZE:
      return "cm";
    case DiagnoseType.BRANCH_LENGTH:
      return "m";
    default:
      return "";
  }
};

/**
 * 複数行を1行へ(LF/CRLF考慮)
 * @param s {string} 処理対象文字列
 * @returns ワンライナー化された文字列
 */
export const makeMultipleLines1Line = (s: string, delim: string = ' ') => s.replace('\r', '').split('\n').join(delim)

/**
 * UTCからローカル時間に変換
 * @param utc {string} UTC文字列
 * @returns ローカル時刻文字列(yyyy-MM-dd HH:mm:ss)
 */
export const toLocalString = (utc: string) => {
  const dateTime = new Date(utc);
  const yyyy = dateTime.getFullYear();
  const MM = ('0' + (dateTime.getMonth() + 1)).slice(-2);
  const dd = ('0' + dateTime.getDate()).slice(-2);
  const HH = ('0' + dateTime.getHours()).slice(-2);
  const mm = ('0' + dateTime.getMinutes()).slice(-2);
  const ss = ('0' + dateTime.getSeconds()).slice(-2);
  const local = yyyy + '-' + MM + '-' + dd + ' ' + HH + ':' + mm + ':' + ss;

  return local;
};
